/**
 * UserDeviceRemapper
 * Maps raw Firestore data to UserDevice DTO and Entity.
 *
 * Flutter source: lib/data/models/user_device.dart — the Dart model has no
 * `@JsonKey(name: ...)` overrides, so Firestore field names equal the
 * property names byte-for-byte (`userId`, `deviceId`, `pushToken`).
 *
 * Read-direction only: the write path goes through `firestoreService.setUserDevice`
 * with a tightened `Pick<UserDeviceDto, 'userId' | 'deviceId'>` shape, matching
 * the codebase convention where remappers handle Firestore → domain only.
 */

import type { UserDeviceDto } from '@/data/dtos/userDevice/userDeviceDto';
import type { JsonObject } from '@/data/types/json';
import type { UserDeviceEntity } from '@/domain/entities/userDevice/userDeviceEntity';

export class UserDeviceRemapper {
  /**
   * Maps raw Firestore data to UserDevice DTO.
   *
   * `userId` and `deviceId` are required Firestore fields (the writer in
   * `firestoreService.setUserDevice` always sets both); missing values fall
   * back to empty strings so the consumer's `dto.deviceId && ...` guard still
   * short-circuits cleanly. `pushToken` stays optional per the DTO contract.
   */
  static fromJson(data: JsonObject): UserDeviceDto {
    return {
      userId: (data['userId'] as string | undefined) || '',
      deviceId: (data['deviceId'] as string | undefined) || '',
      pushToken: data['pushToken'] as string | null | undefined,
    };
  }

  /**
   * Maps raw Firestore data to UserDevice Entity.
   */
  static toEntity(data: any): UserDeviceEntity {
    const dto = this.fromJson(data);
    return { ...dto } as UserDeviceEntity;
  }
}
